import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { cn } from '../lib/cn';

// Набор компонентов в духе shadcn/ui: Tailwind-классы, токены из styles.css.

type ButtonVariant = 'accent' | 'outline' | 'ghost' | 'danger' | 'dashed';

const BTN: Record<ButtonVariant, string> = {
  accent: 'bg-accent text-on-accent hover:bg-accent-hover',
  outline: 'bg-field text-white border border-line-strong hover:border-white/25',
  ghost: 'bg-transparent text-soft hover:bg-white/5 hover:text-white',
  danger: 'bg-bad/15 text-bad border border-bad/30 hover:bg-bad/25',
  dashed: 'bg-transparent text-accent border border-dashed border-accent/40 hover:border-accent',
};

export function Button({
  variant = 'outline',
  size = 'md',
  className,
  busy,
  children,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant; size?: 'sm' | 'md'; busy?: boolean }) {
  return (
    <button
      type="button"
      {...props}
      disabled={props.disabled || busy}
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-xl font-bold whitespace-nowrap transition-colors disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer',
        size === 'md' ? 'h-11 px-[18px] text-sm' : 'h-9 px-3.5 text-sm',
        BTN[variant],
        className,
      )}
    >
      {busy ? <Spinner className="size-4" /> : null}
      {children}
    </button>
  );
}

export function Card({ className, children, ...p }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div {...p} className={cn('min-w-0 rounded-card border border-line bg-card p-5', className)}>
      {children}
    </div>
  );
}

export function CardTitle({ children, right, className }: { children: ReactNode; right?: ReactNode; className?: string }) {
  return (
    <div className={cn('flex items-center justify-between gap-3', className)}>
      <span className="text-base font-bold">{children}</span>
      {right}
    </div>
  );
}

export const Label = ({ children, className }: { children: ReactNode; className?: string }) => (
  <span className={cn('text-[13px] font-medium text-muted', className)}>{children}</span>
);

export function Field({ label, hint, children, className }: { label: ReactNode; hint?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <label className={cn('flex min-w-0 flex-col gap-1.5', className)}>
      <Label>{label}</Label>
      {children}
      {hint ? <span className="text-xs text-dim">{hint}</span> : null}
    </label>
  );
}

const inputCls =
  'w-full rounded-xl border border-white/9 bg-field px-3.5 text-[15px] text-white placeholder:text-dim outline-none focus:border-accent/60 disabled:opacity-60';

export const Input = ({ className, ...p }: React.InputHTMLAttributes<HTMLInputElement>) => (
  <input {...p} className={cn(inputCls, 'h-11', className)} />
);

export const Textarea = ({ className, ...p }: React.TextareaHTMLAttributes<HTMLTextAreaElement>) => (
  <textarea {...p} className={cn(inputCls, 'min-h-24 resize-y py-2.5 leading-relaxed', className)} />
);

export function Select<T extends string>({
  value,
  onChange,
  options,
  className,
  ...p
}: Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'onChange' | 'value'> & {
  value: T;
  onChange: (v: T) => void;
  options: readonly { value: T; label: string; disabled?: boolean }[];
}) {
  return (
    <select {...p} value={value} onChange={(e) => onChange(e.target.value as T)} className={cn(inputCls, 'h-11 cursor-pointer appearance-auto', className)}>
      {options.map((o) => (
        <option key={o.value} value={o.value} disabled={o.disabled}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

export function Chip({ children, className, tone }: { children: ReactNode; className?: string; tone?: Tone }) {
  return (
    <span className={cn('inline-flex h-7 items-center gap-1.5 rounded-lg bg-chip px-2.5 text-[13px] whitespace-nowrap text-text2', tone && TONE[tone], className)}>
      {children}
    </span>
  );
}

export type Tone = 'ok' | 'run' | 'idle' | 'warn' | 'bad';
const TONE: Record<Tone, string> = {
  ok: 'bg-ok/12 text-ok',
  run: 'bg-accent/12 text-accent',
  idle: 'bg-white/8 text-text2',
  warn: 'bg-warn/14 text-[#FFCF85]',
  bad: 'bg-bad/14 text-bad',
};
const DOT: Record<Tone, string> = { ok: 'bg-ok', run: 'bg-accent', idle: 'bg-text2', warn: 'bg-warn', bad: 'bg-bad' };

export function Status({ tone, children, className }: { tone: Tone; children: ReactNode; className?: string }) {
  return (
    <span className={cn('inline-flex h-[26px] items-center gap-1.5 rounded-full px-2.5 text-xs font-semibold whitespace-nowrap', TONE[tone], className)}>
      <span className={cn('size-1.5 rounded-full', DOT[tone])} />
      {children}
    </span>
  );
}

export const Dot = ({ tone, className }: { tone: Tone; className?: string }) => <span className={cn('inline-block size-2 shrink-0 rounded-full', DOT[tone], className)} />;

/** Кнопки-фильтры как в макете «Генерации». */
export function FilterButton({ on, children, ...p }: React.ButtonHTMLAttributes<HTMLButtonElement> & { on?: boolean }) {
  return (
    <button
      type="button"
      {...p}
      className={cn(
        'inline-flex h-9 cursor-pointer items-center gap-2 rounded-[10px] border px-3.5 text-sm font-semibold whitespace-nowrap',
        on ? 'border-accent bg-accent text-on-accent' : 'border-white/8 bg-card text-text2 hover:border-white/20',
        p.className,
      )}
    >
      {children}
    </button>
  );
}

export function Segmented<T extends string>({ value, onChange, options }: { value: T; onChange: (v: T) => void; options: readonly { value: T; label: string }[] }) {
  return (
    <div className="inline-flex rounded-xl border border-line bg-card p-1">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={cn('h-8 cursor-pointer rounded-lg px-3.5 text-sm font-semibold', value === o.value ? 'bg-accent text-on-accent' : 'text-soft hover:text-white')}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Spinner({ className }: { className?: string }) {
  return <span className={cn('inline-block size-5 animate-spin rounded-full border-2 border-white/20 border-t-accent', className)} aria-label="Загрузка" />;
}

export function Loading() {
  return (
    <div className="flex min-h-40 items-center justify-center">
      <Spinner />
    </div>
  );
}

export function ErrorBox({ text, onRetry }: { text: string; onRetry?: () => void }) {
  return (
    <Card className="flex items-center justify-between gap-3 border-bad/30">
      <span className="text-sm text-bad">{text}</span>
      {onRetry ? (
        <Button size="sm" onClick={onRetry}>
          Ещё раз
        </Button>
      ) : null}
    </Card>
  );
}

export const Empty = ({ children }: { children: ReactNode }) => <div className="py-10 text-center text-sm text-muted">{children}</div>;

export function Switch({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label?: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className={cn('relative h-6 w-10 shrink-0 cursor-pointer rounded-full transition-colors', checked ? 'bg-accent' : 'bg-white/15')}
    >
      <span className={cn('absolute top-0.5 size-5 rounded-full bg-white transition-all', checked ? 'left-[18px]' : 'left-0.5')} />
    </button>
  );
}

/** Таблица в стиле макетов. */
export function Table({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className="overflow-x-auto">
      <table className={cn('w-full border-collapse text-sm [&_td]:border-b [&_td]:border-white/5 [&_td]:px-3 [&_td]:py-3 [&_td]:whitespace-nowrap [&_th]:border-b [&_th]:border-line [&_th]:px-3 [&_th]:pt-3 [&_th]:pb-3 [&_th]:text-left [&_th]:text-xs [&_th]:font-medium [&_th]:whitespace-nowrap [&_th]:text-muted', className)}>
        {children}
      </table>
    </div>
  );
}

export function Modal({ open, onClose, title, children }: { open: boolean; onClose: () => void; title: string; children: ReactNode }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal?.();
    if (!open && d.open) d.close?.();
  }, [open]);
  if (!open) return null;
  return (
    <dialog
      ref={ref}
      onClose={onClose}
      onClick={(e) => e.target === ref.current && onClose()}
      className="m-auto max-h-[calc(100dvh-32px)] w-[min(480px,calc(100vw-32px))] overflow-y-auto rounded-card border border-line-strong bg-card p-0 text-white backdrop:bg-black/60"
      open={typeof HTMLDialogElement === 'undefined' ? true : undefined}
    >
      <div className="flex flex-col gap-4 p-5">
        <div className="flex items-center justify-between gap-3">
          <span className="text-lg font-bold">{title}</span>
          <button type="button" onClick={onClose} className="cursor-pointer text-2xl leading-none text-muted hover:text-white" aria-label="Закрыть">
            ×
          </button>
        </div>
        {children}
      </div>
    </dialog>
  );
}

// ---------- Тосты ----------

interface Toast {
  id: number;
  text: string;
  tone: 'ok' | 'bad';
}
const ToastCtx = createContext<(text: string, tone?: 'ok' | 'bad') => void>(() => {});
export const useToast = () => useContext(ToastCtx);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Toast[]>([]);
  const push = useCallback((text: string, tone: 'ok' | 'bad' = 'ok') => {
    const id = Date.now() + Math.random();
    setItems((s) => [...s, { id, text, tone }]);
    setTimeout(() => setItems((s) => s.filter((t) => t.id !== id)), 4000);
  }, []);
  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div className="pointer-events-none fixed inset-x-0 bottom-24 z-50 flex flex-col items-center gap-2 px-4 lg:bottom-6">
        {items.map((t) => (
          <div
            key={t.id}
            role="status"
            className={cn('pointer-events-auto rounded-xl border px-4 py-3 text-sm font-semibold shadow-lg', t.tone === 'ok' ? 'border-ok/30 bg-[#0d1d19] text-ok' : 'border-bad/30 bg-[#22111a] text-bad')}
          >
            {t.text}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

/** Шапка страницы: «хлебные крошки», заголовок, действия. */
export function PageHead({ crumbs, title, badge, actions }: { crumbs?: ReactNode; title: ReactNode; badge?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3.5">
      <div className="flex min-w-0 flex-col gap-1.5">
        {crumbs ? <Label>{crumbs}</Label> : null}
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="m-0 text-2xl font-extrabold tracking-tight lg:text-[30px]">{title}</h1>
          {badge}
        </div>
      </div>
      {actions ? <div className="flex flex-wrap gap-2.5">{actions}</div> : null}
    </div>
  );
}

export function Kpi({ label, value, sub, className }: { label: string; value: ReactNode; sub?: ReactNode; className?: string }) {
  return (
    <Card className={cn('flex flex-col gap-2', className)}>
      <Label>{label}</Label>
      <b className="text-[26px] leading-none font-extrabold tracking-tight">{value}</b>
      {sub ? <span className="text-[13px] text-muted">{sub}</span> : null}
    </Card>
  );
}
